import {useState} from "react";

/**
 * Interactive "user experience" walkthrough — replicates the design's mobile
 * escalation flow (L1 push → L2 ASSIST call → L3 SMS → L4 phone → confirmed).
 * Purely presentational; ported from the design with inline styles to keep the
 * device mockups pixel-accurate.
 */

const truncate = (text: string, max: number) => (text.length > max ? `${text.substring(0, max)}…` : text);

const BellGlyph = ({size = 12, color = "#fff"}: { size?: number; color?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size}>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
);

const PhoneIcon = ({size = 16, color = "#fff"}: { size?: number; color?: string }) => (
    <svg viewBox="0 0 24 24" fill={color} width={size} height={size}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.44a2 2 0 0 1 1.97-2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.29 6.29l.88-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
);

const PhoneStatusBar = () => (
    <div style={{height: 32, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '0 16px 6px', position: 'relative', zIndex: 10}}>
        <span style={{fontSize: 11, fontWeight: 600, color: '#fff'}}>9:41</span>
        <div style={{display: 'flex', gap: 4, alignItems: 'center'}}>
            <svg viewBox="0 0 17 12" fill="#fff" width="13" height="9"><rect x="0" y="6" width="3" height="6" rx="1"/><rect x="4.5" y="4" width="3" height="8" rx="1"/><rect x="9" y="2" width="3" height="10" rx="1"/><rect x="13.5" y="0" width="3" height="12" rx="1"/></svg>
            <div style={{width: 18, height: 9, borderRadius: 2, border: '1.2px solid rgba(255,255,255,0.7)', position: 'relative', overflow: 'hidden'}}>
                <div style={{position: 'absolute', left: 1, top: 1, bottom: 1, borderRadius: 1, background: '#fff', width: '75%'}}/>
            </div>
        </div>
    </div>
);

const PhoneHomeScreen = ({alertTitle, alertMessage, onTrigger}: {
    alertTitle: string;
    alertMessage: string;
    onTrigger: () => void;
}) => (
    <div style={{flex: 1, background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', display: 'flex', flexDirection: 'column', position: 'relative'}}>
        <PhoneStatusBar/>
        <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 14px 0'}}>
            <div style={{fontSize: 28, fontWeight: 200, color: '#fff', marginBottom: 2}}>9:41</div>
            <div style={{fontSize: 11, color: 'rgba(255,255,255,0.6)'}}>Wednesday, 23 April</div>
        </div>
        <div style={{position: 'absolute', top: 38, left: 8, right: 8, animation: 'ensFlowSlideDown 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards'}}>
            <div
                style={{display: 'block', width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px)', borderRadius: 12, padding: '8px 10px', boxShadow: '0 6px 24px rgba(0,0,0,0.25)', textAlign: 'left'}}
            >
                <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5}}>
                    <div style={{width: 22, height: 22, borderRadius: 5, background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                        <BellGlyph size={11}/>
                    </div>
                    <div style={{flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span style={{fontSize: 9, fontWeight: 700, color: 'rgb(24,24,27)', letterSpacing: '0.02em'}}>PHONEHQ · EMERGENCY</span>
                        <span style={{fontSize: 8, color: 'rgb(159,159,169)'}}>now</span>
                    </div>
                </div>
                <div style={{fontSize: 10, fontWeight: 700, color: 'rgb(24,24,27)', marginBottom: 2}}>{truncate(alertTitle, 32)}</div>
                <div style={{fontSize: 9, color: 'rgb(113,113,123)', lineHeight: '1.3', marginBottom: 7}}>{truncate(alertMessage, 58)}</div>
                <div style={{display: 'flex', gap: 6}}>
                    <button
                        type="button"
                        style={{flex: 1, height: 24, borderRadius: 6, border: '1px solid rgb(230,230,230)', background: '#fff', fontSize: 9, fontWeight: 700, color: 'rgb(113,113,123)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center'}}
                    >
                        Dismiss
                    </button>
                    <button
                        type="button"
                        onClick={onTrigger}
                        style={{flex: 1, height: 24, borderRadius: 6, border: 'none', background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', fontSize: 9, fontWeight: 700, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center'}}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    </div>
);

const L1ConfirmScreen = ({alertTitle, alertMessage, onConfirm}: { alertTitle: string; alertMessage: string; onConfirm: () => void }) => (
    <div style={{flex: 1, background: '#fff', display: 'flex', flexDirection: 'column'}}>
        <div style={{background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', paddingBottom: 12}}>
            <PhoneStatusBar/>
            <div style={{padding: '0 12px 4px', display: 'flex', alignItems: 'center', gap: 7}}>
                <div style={{width: 28, height: 28, borderRadius: 7, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <BellGlyph size={15}/>
                </div>
                <div>
                    <div style={{fontSize: 8, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600}}>Emergency Alert</div>
                    <div style={{fontSize: 12, fontWeight: 700, color: '#fff'}}>Action required</div>
                </div>
            </div>
        </div>
        <div style={{flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', gap: 10}}>
            <div style={{borderRadius: 10, border: '1px solid rgb(230,230,230)', padding: '10px', background: '#fafafa'}}>
                <div style={{fontSize: 11, fontWeight: 700, color: 'rgb(24,24,27)', marginBottom: 4, lineHeight: '1.3'}}>{alertTitle}</div>
                <div style={{fontSize: 9, color: 'rgb(113,113,123)', lineHeight: '1.4'}}>{truncate(alertMessage, 80)}</div>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: 5, padding: '6px 8px', borderRadius: 6, background: '#fffbeb', border: '1px solid #fde68a'}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span style={{fontSize: 8, color: '#b45309', fontWeight: 500}}>30s to confirm before escalation</span>
            </div>
            <div style={{fontSize: 8, color: 'rgb(159,159,169)', textAlign: 'center'}}>By confirming, you acknowledge receipt.</div>
        </div>
        <div style={{padding: '8px 12px 16px', background: '#fff', borderTop: '1px solid rgb(230,230,230)'}}>
            <button onClick={onConfirm} style={{width: '100%', height: 38, borderRadius: 10, border: 'none', background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer'}}>
                Confirm receipt
            </button>
        </div>
    </div>
);

const L2AssistScreen = ({alertTitle, onConfirm, onReject}: { alertTitle: string; onConfirm: () => void; onReject: () => void }) => {
    const [answered, setAnswered] = useState(false);
    if (answered) {
        return (
            <div style={{flex: 1, background: '#fff', display: 'flex', flexDirection: 'column'}}>
                <div style={{background: '#18181b', paddingBottom: 10}}>
                    <PhoneStatusBar/>
                    <div style={{padding: '0 12px', display: 'flex', alignItems: 'center', gap: 7}}>
                        <div style={{width: 32, height: 32, borderRadius: '50%', background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <PhoneIcon size={16}/>
                        </div>
                        <div>
                            <div style={{fontSize: 12, fontWeight: 700, color: '#fff'}}>PhoneHQ ASSIST</div>
                            <div style={{fontSize: 9, color: '#00c950'}}>● In call · 00:12</div>
                        </div>
                    </div>
                </div>
                <div style={{flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', gap: 10}}>
                    <div style={{padding: '10px', borderRadius: 10, background: '#fafafa', border: '1px solid rgb(230,230,230)'}}>
                        <div style={{fontSize: 8, fontWeight: 600, color: '#9747ff', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4}}>Alert message</div>
                        <div style={{fontSize: 10, color: 'rgb(24,24,27)', lineHeight: '1.4'}}>{truncate(alertTitle, 60)}</div>
                    </div>
                    <div style={{fontSize: 9, color: 'rgb(113,113,123)', textAlign: 'center', lineHeight: '1.4'}}>Review the alert and tap below to confirm receipt.</div>
                </div>
                <div style={{padding: '8px 12px 16px', borderTop: '1px solid rgb(230,230,230)'}}>
                    <button onClick={onConfirm} style={{width: '100%', height: 38, borderRadius: 10, border: 'none', background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer'}}>Confirm receipt</button>
                </div>
            </div>
        );
    }
    return (
        <div style={{flex: 1, background: 'linear-gradient(180deg, #2c2c34 0%, #1f1f26 100%)', display: 'flex', flexDirection: 'column'}}>
            <PhoneStatusBar/>
            <div style={{padding: '8px 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderBottom: '1px solid rgba(255,255,255,0.08)'}}>
                <span style={{fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 500}}>End-to-end encrypted</span>
            </div>
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '14px', gap: 10}}>
                <div style={{width: 70, height: 70, borderRadius: 16, background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4}}>
                    <BellGlyph size={32}/>
                </div>
                <div style={{textAlign: 'center', marginBottom: 2}}>
                    <div style={{fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2}}>PhoneHQ Alert</div>
                    <div style={{fontSize: 10, color: 'rgba(255,255,255,0.5)'}}>Calling…</div>
                </div>
                <div style={{maxWidth: 160, padding: '7px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'center'}}>
                    <div style={{fontSize: 7, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 3}}>L2 Escalation · ASSIST</div>
                    <div style={{fontSize: 9, color: 'rgba(255,255,255,0.8)', lineHeight: '1.3'}}>{truncate(alertTitle, 40)}</div>
                </div>
            </div>
            <div style={{padding: '0 24px 18px', display: 'flex', justifyContent: 'center', gap: 24}}>
                <button onClick={onReject} style={{width: 50, height: 50, borderRadius: '50%', border: 'none', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 3px 10px rgba(239,68,68,0.4)'}}>
                    <PhoneIcon size={22}/>
                </button>
            </div>
            <div style={{padding: '0 20px 20px'}}>
                <button onClick={() => setAnswered(true)} style={{width: '100%', height: 38, borderRadius: 12, border: 'none', background: '#00c950', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: '0 3px 10px rgba(0,201,80,0.4)'}}>
                    <PhoneIcon size={16}/>
                    Answer
                </button>
            </div>
        </div>
    );
};

const L3SMSScreen = ({onConfirm}: { onConfirm: () => void }) => {
    const [reply, setReply] = useState('');
    const [sent, setSent] = useState(false);
    const handleSend = () => {
        if (!reply.trim()) return;
        setSent(true);
        setTimeout(onConfirm, 900);
    };
    return (
        <div style={{flex: 1, background: '#fff', display: 'flex', flexDirection: 'column'}}>
            <div style={{background: '#f9f9f9', borderBottom: '1px solid rgb(230,230,230)'}}>
                <PhoneStatusBar/>
                <div style={{padding: '4px 12px 8px', display: 'flex', alignItems: 'center', gap: 7}}>
                    <div style={{width: 28, height: 28, borderRadius: '50%', background: '#9747ff', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    </div>
                    <div>
                        <div style={{fontSize: 11, fontWeight: 700, color: 'rgb(24,24,27)'}}>PhoneHQ Alerts</div>
                        <div style={{fontSize: 8, color: 'rgb(113,113,123)'}}>+1 (555) 000-ALERT · L3</div>
                    </div>
                </div>
            </div>
            <div style={{flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 8}}>
                <div style={{display: 'flex', justifyContent: 'flex-start'}}>
                    <div style={{maxWidth: '78%', padding: '8px 10px', borderRadius: '12px 12px 12px 2px', background: '#f4f4f5', fontSize: 10, color: 'rgb(24,24,27)', lineHeight: '1.4'}}>
                        [PhoneHQ] URGENT: Suspicious login detected. Reply "1" or "ACK" to confirm receipt.
                        <div style={{fontSize: 8, color: 'rgb(159,159,169)', marginTop: 3, textAlign: 'right'}}>14:37</div>
                    </div>
                </div>
                {sent && (
                    <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                        <div style={{maxWidth: '60%', padding: '8px 10px', borderRadius: '12px 12px 2px 12px', background: 'radial-gradient(rgb(97,95,255) 0%, rgb(79,57,246) 100%)', fontSize: 10, color: '#fff'}}>
                            {reply}
                            <div style={{fontSize: 8, color: 'rgba(255,255,255,0.6)', marginTop: 3, textAlign: 'right'}}>14:38 ✓</div>
                        </div>
                    </div>
                )}
            </div>
            <div style={{padding: '6px 10px 16px', borderTop: '1px solid rgb(230,230,230)', display: 'flex', gap: 6, alignItems: 'center'}}>
                <div style={{display: 'flex', gap: 4}}>
                    {['1', 'ACK', 'yes'].map((q) => (
                        <button key={q} onClick={() => setReply(q)} style={{height: 22, padding: '0 8px', borderRadius: 9999, border: '1px solid #9747ff', background: reply === q ? '#9747ff' : '#fff', color: reply === q ? '#fff' : '#9747ff', fontSize: 9, fontWeight: 700, cursor: 'pointer'}}>{q}</button>
                    ))}
                </div>
                <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Message" style={{flex: 1, height: 28, borderRadius: 14, border: '1px solid rgb(230,230,230)', padding: '0 10px', fontSize: 10, outline: 'none'}}/>
                <button onClick={handleSend} disabled={!reply.trim()} style={{width: 28, height: 28, borderRadius: '50%', border: 'none', background: reply.trim() ? '#9747ff' : '#f4f4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: reply.trim() ? 'pointer' : 'default'}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke={reply.trim() ? '#fff' : 'rgb(159,159,169)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </button>
            </div>
        </div>
    );
};

const L4PhoneScreen = ({onConfirm}: { onConfirm: () => void }) => {
    const [pressed, setPressed] = useState(false);
    return (
        <div style={{flex: 1, background: '#1a1a2e', display: 'flex', flexDirection: 'column'}}>
            <PhoneStatusBar/>
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '14px', gap: 12}}>
                <div style={{width: 60, height: 60, borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 10px rgba(239,68,68,0.15), 0 0 0 20px rgba(239,68,68,0.07)', animation: 'ensFlowPulse 1.5s ease-in-out infinite'}}>
                    <PhoneIcon size={28}/>
                </div>
                <div style={{textAlign: 'center', marginBottom: 2}}>
                    <div style={{fontSize: 8, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4}}>Phone Call · L4 Final</div>
                    <div style={{fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 4}}>PhoneHQ Emergency</div>
                    <div style={{fontSize: 9, color: 'rgba(255,255,255,0.5)', lineHeight: '1.4', maxWidth: 160}}>Automated call. Press 1 to confirm.</div>
                </div>
                {!pressed ? (
                    <div style={{width: '100%', padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', textAlign: 'center'}}>
                        <div style={{fontSize: 9, color: 'rgba(255,255,255,0.75)', marginBottom: 10, lineHeight: '1.4'}}>"Press 1 to confirm receipt."</div>
                        <div style={{display: 'flex', justifyContent: 'center', gap: 8}}>
                            {[1, 2, 3].map((n) => (
                                <button
                                    key={n}
                                    onClick={() => {
                                        if (n === 1) {
                                            setPressed(true);
                                            setTimeout(onConfirm, 1000);
                                        }
                                    }}
                                    style={{width: 36, height: 36, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: n === 1 ? 'rgba(151,71,255,0.4)' : 'rgba(255,255,255,0.07)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer'}}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div style={{padding: '10px 14px', borderRadius: 10, background: 'rgba(0,201,80,0.15)', border: '1px solid rgba(0,201,80,0.3)', textAlign: 'center'}}>
                        <div style={{fontSize: 11, fontWeight: 700, color: '#00c950', marginBottom: 3}}>Confirmed!</div>
                        <div style={{fontSize: 9, color: 'rgba(255,255,255,0.6)'}}>Receipt logged. Ending call…</div>
                    </div>
                )}
            </div>
        </div>
    );
};

const ConfirmedScreen = ({level, method}: { level: number; method: string }) => (
    <div style={{flex: 1, background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center'}}>
        <div style={{width: 48, height: 48, borderRadius: '50%', background: '#00c950', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, animation: 'ensFlowPopIn 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards'}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{fontSize: 15, fontWeight: 700, color: 'rgb(24,24,27)', marginBottom: 6}}>Receipt confirmed</div>
        <div style={{fontSize: 10, color: 'rgb(113,113,123)', lineHeight: '1.5', marginBottom: 14}}>
            You acknowledged this alert via <strong style={{color: 'rgb(24,24,27)'}}>{method}</strong> at L{level}.
        </div>
        <div style={{padding: '6px 12px', borderRadius: 8, background: '#f4f4f5', fontSize: 9, fontWeight: 600, color: 'rgb(113,113,123)'}}>
            {new Date().toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', second: '2-digit'})}
        </div>
    </div>
);

type ScreenKey = 'home' | 'l1' | 'l2' | 'l3' | 'l4';

export const UserFlowDemo = ({alertTitle, alertMessage, startLevel}: {
    alertTitle: string;
    alertMessage: string;
    startLevel: number;
}) => {
    const initial: ScreenKey = startLevel === 2 ? 'l2' : startLevel === 3 ? 'l3' : startLevel === 4 ? 'l4' : 'home';
    const [screen, setScreen] = useState<ScreenKey>(initial);
    const [confirmed, setConfirmed] = useState<{ level: number; method: string } | null>(null);

    let body;
    if (confirmed) {
        body = <ConfirmedScreen level={confirmed.level} method={confirmed.method}/>;
    } else if (screen === 'home') {
        body = <PhoneHomeScreen alertTitle={alertTitle} alertMessage={alertMessage} onTrigger={() => setScreen('l1')}/>;
    } else if (screen === 'l1') {
        body = <L1ConfirmScreen alertTitle={alertTitle} alertMessage={alertMessage} onConfirm={() => setConfirmed({level: 1, method: 'Push notification'})}/>;
    } else if (screen === 'l2') {
        body = <L2AssistScreen alertTitle={alertTitle} onConfirm={() => setConfirmed({level: 2, method: 'ASSIST call'})} onReject={() => setScreen('l3')}/>;
    } else if (screen === 'l3') {
        body = <L3SMSScreen onConfirm={() => setConfirmed({level: 3, method: 'SMS reply'})}/>;
    } else {
        body = <L4PhoneScreen onConfirm={() => setConfirmed({level: 4, method: 'Phone call'})}/>;
    }

    return (
        <div style={{width: 248, height: 504, borderRadius: 34, background: '#000', boxShadow: '0 20px 50px rgba(0,0,0,0.35), inset 0 0 0 2px #2a2a2e', overflow: 'hidden', flexShrink: 0, display: 'flex', flexDirection: 'column'}}>
            {body}
        </div>
    );
};
